import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import Icon from '../../components/AppIcon';
import PolicySection from './components/PolicySection';
import TableOfContents from './components/TableOfContents';
import Footer from '../homepage-sales-landing/components/Footer';

const PrivacyPolicy = () => {
  const [activeSection, setActiveSection] = useState('introduction');

  const sections = [
    {
      id: 'introduction',
      title: 'Introduction',
      icon: 'FileText',
      content: (
        <>
          <p className="mb-4">
            Welcome to AuthorPreneur AI. This Privacy Policy explains how Entrepreneur Power Network, LLC ("we," "us," or "our") collects, uses, discloses, and protects your personal information when you use our platform located at 813 N. Main Street, Suite 502, McAllen, TX 78501.
          </p>
          <p className="mb-4">
            By using AuthorPreneur AI, you agree to the collection and use of information in accordance with this policy. We are committed to protecting your privacy and ensuring transparency in our data handling practices.
          </p>
          <p>
            This policy applies to all users of our platform, including free trial users, subscribers, and visitors to our website.
          </p>
        </>
      )
    },
    {
      id: 'data-collection',
      title: 'Information We Collect',
      icon: 'Database',
      content: (
        <>
          <h3 className="text-lg font-semibold text-foreground mb-3">Personal Information</h3>
          <ul className="list-disc list-inside mb-4 space-y-2 text-muted-foreground">
            <li>Name, email address, and contact information</li>
            <li>Account credentials and authentication data</li>
            <li>Payment information processed through Stripe</li>
            <li>Subscription tier and billing history</li>
          </ul>

          <h3 className="text-lg font-semibold text-foreground mb-3 mt-6">Content and Usage Data</h3>
          <ul className="list-disc list-inside mb-4 space-y-2 text-muted-foreground">
            <li>Book manuscripts, outlines, and writing projects</li>
            <li>AI interaction history and generated content</li>
            <li>Platform usage patterns and feature engagement</li>
            <li>Device information and browser data</li>
          </ul>

          <h3 className="text-lg font-semibold text-foreground mb-3 mt-6">Technical Information</h3>
          <ul className="list-disc list-inside space-y-2 text-muted-foreground">
            <li>IP addresses and location data</li>
            <li>Cookies and similar tracking technologies</li>
            <li>Log files and analytics data</li>
            <li>Performance and diagnostic information</li>
          </ul>
        </>
      )
    },
    {
      id: 'data-usage',
      title: 'How We Use Your Information',
      icon: 'Settings',
      content: (
        <>
          <p className="mb-4">
            We use the collected information for the following purposes:
          </p>
          <ul className="list-disc list-inside space-y-3 text-muted-foreground">
            <li>
              <strong className="text-foreground">Service Delivery:</strong> To provide, maintain, and improve AuthorPreneur AI platform features
            </li>
            <li>
              <strong className="text-foreground">AI Processing:</strong> To process your content through ChatGPT API for book writing assistance
            </li>
            <li>
              <strong className="text-foreground">Payment Processing:</strong> To handle subscription payments securely through Stripe
            </li>
            <li>
              <strong className="text-foreground">Communication:</strong> To send important updates, notifications, and marketing materials (with your consent)
            </li>
            <li>
              <strong className="text-foreground">Community Access:</strong> To manage your access to Skool community integration
            </li>
            <li>
              <strong className="text-foreground">Analytics:</strong> To understand usage patterns and improve user experience
            </li>
            <li>
              <strong className="text-foreground">Security:</strong> To protect against fraud, abuse, and unauthorized access
            </li>
          </ul>
        </>
      )
    },
    {
      id: 'data-sharing',
      title: 'Data Sharing and Third-Party Services',
      icon: 'Share2',
      content: (
        <>
          <h3 className="text-lg font-semibold text-foreground mb-3">Third-Party Integrations</h3>
          <div className="space-y-4 mb-6">
            <div className="bg-muted/50 rounded-lg p-4">
              <h4 className="font-semibold text-foreground mb-2">OpenAI (ChatGPT API)</h4>
              <p className="text-sm text-muted-foreground">
                Your book content is processed through OpenAI's API for AI-assisted writing. OpenAI's data usage policy applies to this processing.
              </p>
            </div>
            <div className="bg-muted/50 rounded-lg p-4">
              <h4 className="font-semibold text-foreground mb-2">Stripe Payments</h4>
              <p className="text-sm text-muted-foreground">
                Payment information is processed and stored by Stripe according to their security standards and privacy policy.
              </p>
            </div>
            <div className="bg-muted/50 rounded-lg p-4">
              <h4 className="font-semibold text-foreground mb-2">Skool Community</h4>
              <p className="text-sm text-muted-foreground">
                Community access and participation data may be shared with Skool platform for membership management.
              </p>
            </div>
          </div>

          <h3 className="text-lg font-semibold text-foreground mb-3">When We Share Data</h3>
          <ul className="list-disc list-inside space-y-2 text-muted-foreground">
            <li>With service providers who assist in platform operations</li>
            <li>When required by law or legal process</li>
            <li>To protect rights, property, or safety</li>
            <li>With your explicit consent</li>
          </ul>
        </>
      )
    },
    {
      id: 'data-security',
      title: 'Data Security and Protection',
      icon: 'Shield',
      content: (
        <>
          <p className="mb-4">
            We implement industry-standard security measures to protect your personal information:
          </p>
          <ul className="list-disc list-inside space-y-2 text-muted-foreground mb-6">
            <li>SSL encryption for data transmission</li>
            <li>Secure storage with access controls</li>
            <li>Regular security audits and updates</li>
            <li>Bank-level security for payment processing</li>
            <li>Encrypted backups and disaster recovery</li>
          </ul>

          <div className="bg-amber-50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-800 rounded-lg p-4">
            <div className="flex items-start space-x-3">
              <Icon name="AlertCircle" size={20} color="var(--color-warning)" className="flex-shrink-0 mt-0.5" />
              <p className="text-sm text-muted-foreground">
                While we strive to protect your information, no method of transmission over the internet is 100% secure. We cannot guarantee absolute security but continuously work to maintain the highest standards.
              </p>
            </div>
          </div>
        </>
      )
    },
    {
      id: 'manuscript-protection',
      title: 'Book Manuscript Protection',
      icon: 'BookOpen',
      content: (
        <>
          <p className="mb-4">
            Your book manuscripts and creative content are treated with special care:
          </p>
          <ul className="list-disc list-inside space-y-2 text-muted-foreground mb-6">
            <li>Manuscripts are stored securely and remain your intellectual property</li>
            <li>AI processing does not transfer ownership of your content</li>
            <li>We do not use your manuscripts to train AI models</li>
            <li>Content is not shared with other users or third parties without permission</li>
            <li>You can export and delete your content at any time</li>
          </ul>

          <div className="bg-green-50 dark:bg-green-950/20 border border-green-200 dark:border-green-800 rounded-lg p-4">
            <div className="flex items-start space-x-3">
              <Icon name="CheckCircle" size={20} color="var(--color-success)" className="flex-shrink-0 mt-0.5" />
              <p className="text-sm text-muted-foreground">
                You retain full copyright and ownership of all content you create using AuthorPreneur AI.
              </p>
            </div>
          </div>
        </>
      )
    },
    {
      id: 'user-rights',
      title: 'Your Rights and Choices',
      icon: 'UserCheck',
      content: (
        <>
          <h3 className="text-lg font-semibold text-foreground mb-3">Under GDPR and CCPA</h3>
          <p className="mb-4">You have the following rights regarding your personal data:</p>
          <ul className="list-disc list-inside space-y-3 text-muted-foreground mb-6">
            <li>
              <strong className="text-foreground">Access:</strong> Request a copy of your personal data
            </li>
            <li>
              <strong className="text-foreground">Rectification:</strong> Correct inaccurate or incomplete data
            </li>
            <li>
              <strong className="text-foreground">Erasure:</strong> Request deletion of your data ("right to be forgotten")
            </li>
            <li>
              <strong className="text-foreground">Portability:</strong> Receive your data in a structured format
            </li>
            <li>
              <strong className="text-foreground">Restriction:</strong> Limit how we process your data
            </li>
            <li>
              <strong className="text-foreground">Objection:</strong> Object to certain processing activities
            </li>
            <li>
              <strong className="text-foreground">Withdraw Consent:</strong> Revoke consent for marketing communications
            </li>
          </ul>

          <h3 className="text-lg font-semibold text-foreground mb-3">How to Exercise Your Rights</h3>
          <p className="text-muted-foreground mb-4">
            To exercise any of these rights, contact us at{' '}
            <a href="mailto:success@entrepreneurpowernetwork.com" className="text-primary hover:underline">
              success@entrepreneurpowernetwork.com
            </a>
            . We will respond to your request within 30 days.
          </p>
        </>
      )
    },
    {
      id: 'data-retention',
      title: 'Data Retention',
      icon: 'Clock',
      content: (
        <>
          <p className="mb-4">
            We retain your information for as long as necessary to provide our services and comply with legal obligations:
          </p>
          <ul className="list-disc list-inside space-y-2 text-muted-foreground mb-6">
            <li>Account data: Until account deletion or 90 days after subscription cancellation</li>
            <li>Manuscript content: Until you delete projects or close your account</li>
            <li>Payment records: 7 years for tax and accounting purposes</li>
            <li>Marketing consent: Until you opt out or 2 years of inactivity</li>
            <li>Analytics data: Aggregated and anonymized after 26 months</li>
          </ul>

          <p className="text-muted-foreground">
            After account closure, we may retain certain information for legal compliance, dispute resolution, and enforcing our terms.
          </p>
        </>
      )
    },
    {
      id: 'cookies',
      title: 'Cookies and Tracking',
      icon: 'Cookie',
      content: (
        <>
          <p className="mb-4">
            We use cookies and similar technologies to enhance your experience:
          </p>
          <ul className="list-disc list-inside space-y-2 text-muted-foreground mb-6">
            <li><strong className="text-foreground">Essential cookies:</strong> Required for platform functionality</li>
            <li><strong className="text-foreground">Analytics cookies:</strong> Help us understand usage patterns</li>
            <li><strong className="text-foreground">Preference cookies:</strong> Remember your settings and choices</li>
            <li><strong className="text-foreground">Marketing cookies:</strong> Used for targeted advertising (with consent)</li>
          </ul>

          <p className="text-muted-foreground mb-4">
            You can control cookie preferences through your browser settings. Note that disabling certain cookies may affect platform functionality.
          </p>

          <Link to="/cookie-policy" className="text-primary hover:underline inline-flex items-center">
            View detailed Cookie Policy
            <Icon name="ArrowRight" size={16} className="ml-1" />
          </Link>
        </>
      )
    },
    {
      id: 'international',
      title: 'International Data Transfers',
      icon: 'Globe',
      content: (
        <>
          <p className="mb-4">
            AuthorPreneur AI operates globally. Your information may be transferred to and processed in countries other than your own:
          </p>
          <ul className="list-disc list-inside space-y-2 text-muted-foreground mb-4">
            <li>We ensure adequate protection through standard contractual clauses</li>
            <li>Data transfers comply with GDPR and other applicable regulations</li>
            <li>Third-party services may process data in various locations</li>
          </ul>
          <p className="text-muted-foreground">
            By using our platform, you consent to the transfer of your information to the United States and other countries where we operate.
          </p>
        </>
      )
    },
    {
      id: 'children',
      title: 'Children\'s Privacy',
      icon: 'Users',
      content: (
        <>
          <p className="mb-4">
            AuthorPreneur AI is not intended for users under 16 years of age. We do not knowingly collect personal information from children.
          </p>
          <p className="text-muted-foreground">
            If we discover that we have collected information from a child under 16, we will promptly delete such information. If you believe we may have collected data from a minor, please contact us immediately.
          </p>
        </>
      )
    },
    {
      id: 'changes',
      title: 'Policy Updates',
      icon: 'RefreshCw',
      content: (
        <>
          <p className="mb-4">
            We may update this Privacy Policy periodically to reflect changes in our practices or legal requirements. When we make significant changes:
          </p>
          <ul className="list-disc list-inside space-y-2 text-muted-foreground mb-4">
            <li>We will notify you via email or platform notification</li>
            <li>The "Last Updated" date at the top will be revised</li>
            <li>Continued use of the platform constitutes acceptance of changes</li>
          </ul>
          <p className="text-muted-foreground">
            We encourage you to review this policy regularly to stay informed about how we protect your information.
          </p>
        </>
      )
    },
    {
      id: 'contact',
      title: 'Contact Information',
      icon: 'Mail',
      content: (
        <>
          <p className="mb-6">
            If you have questions, concerns, or requests regarding this Privacy Policy or your personal data, please contact us:
          </p>
          <div className="bg-card border border-border rounded-lg p-6 space-y-4">
            <div>
              <h4 className="font-semibold text-foreground mb-2">Email</h4>
              <a href="mailto:success@entrepreneurpowernetwork.com" className="text-primary hover:underline">
                success@entrepreneurpowernetwork.com
              </a>
            </div>
            <div>
              <h4 className="font-semibold text-foreground mb-2">Mailing Address</h4>
              <p className="text-muted-foreground">
                Entrepreneur Power Network, LLC<br />
                813 N. Main Street, Suite 502<br />
                McAllen, TX 78501
              </p>
            </div>
            <div>
              <h4 className="font-semibold text-foreground mb-2">Data Protection Officer</h4>
              <p className="text-muted-foreground">
                For GDPR-related inquiries, you may contact our Data Protection Officer at the email address above.
              </p>
            </div>
          </div>
        </>
      )
    }
  ];

  return (
    <>
      <Helmet>
        <title>Privacy Policy - AuthorPreneur AI</title>
        <meta name="description" content="Learn how AuthorPreneur AI collects, uses, and protects your personal information" />
      </Helmet>

      <div className="min-h-screen bg-background">
        {/* Header */}
        <header className="border-b border-border bg-card sticky top-0 z-50">
          <div className="container mx-auto px-4 py-4">
            <div className="flex items-center justify-between">
              <Link to="/" className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                  <Icon name="BookOpen" size={24} color="var(--color-primary)" />
                </div>
                <span className="text-xl font-heading font-semibold text-foreground">
                  AuthorPreneur AI
                </span>
              </Link>
              <Link 
                to="/legal-pages-hub"
                className="text-sm text-muted-foreground hover:text-primary flex items-center"
              >
                <Icon name="ArrowLeft" size={16} className="mr-1" />
                Back to Legal Hub
              </Link>
            </div>
          </div>
        </header>

        {/* Main Content */}
        <main className="container mx-auto px-4 py-12">
          <div className="max-w-6xl mx-auto">
            {/* Page Header */}
            <div className="mb-12">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-primary/10 rounded-2xl mb-6">
                <Icon name="Shield" size={32} color="var(--color-primary)" />
              </div>
              <h1 className="text-4xl lg:text-5xl font-heading font-bold text-foreground mb-4">
                Privacy Policy
              </h1>
              <p className="text-lg text-muted-foreground mb-2">
                Last Updated: December 8, 2025
              </p>
              <p className="text-muted-foreground">
                Effective Date: December 8, 2025
              </p>
            </div>

            <div className="grid lg:grid-cols-4 gap-8">
              {/* Table of Contents - Sidebar */}
              <div className="lg:col-span-1">
                <TableOfContents 
                  sections={sections} 
                  activeSection={activeSection}
                  onSectionClick={setActiveSection}
                />
              </div>

              {/* Policy Content */}
              <div className="lg:col-span-3 space-y-8">
                {sections?.map((section) => (
                  <PolicySection
                    key={section?.id}
                    section={section}
                    isActive={activeSection === section?.id}
                  />
                ))}

                {/* Download Option */}
                <div className="bg-muted/50 border border-border rounded-xl p-6 mt-12">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="text-lg font-semibold text-foreground mb-2">
                        Download PDF Version
                      </h3>
                      <p className="text-sm text-muted-foreground">
                        Save a copy of our privacy policy for offline reference
                      </p>
                    </div>
                    <button className="flex items-center space-x-2 bg-primary text-primary-foreground px-4 py-2 rounded-lg hover:bg-primary/90 transition-colors">
                      <Icon name="Download" size={18} />
                      <span className="text-sm font-medium">Download</span>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </main>

        <Footer />
      </div>
    </>
  );
};

export default PrivacyPolicy;